import React, { useState } from 'react';
import LeftSidebar from './leftsidebar';
import RightSidebar from './rightsidebar';
import { ThumbsUp, MessageSquare } from "lucide-react";

const FeedPage = () => {
  // Sample initial feed posts with LinkedIn-like structure + Gen Z twist
  const initialPosts = [
    {
      id: 1,
      user: {
        name: 'girlsoncampus',
        avatar: '/api/placeholder/40/40',
        role: 'Official Campus Account'
      },
      category: 'Scholarship',
      title: 'New $5,000 STEM Scholarship Announced!',
      content: 'Exciting news for aspiring STEM students! A new $5,000 scholarship has just been announced for the upcoming academic year. The deadline to apply is May 15th, 2025. Check the link in bio for more details and eligibility criteria. #Scholarship #STEM #CollegeFunding',
      timestamp: '2 hours ago',
      likes: 42,
      comments: [
        { id: 101, user: 'sarah_stem', content: 'This is exactly what I needed! Thanks for sharing!', timestamp: '1 hour ago' },
        { id: 102, user: 'future_engineer', content: 'Does anyone know if international students are eligible?', timestamp: '30 mins ago' }
      ],
      liked: false
    },
    {
      id: 2,
      user: {
        name: 'MariaSilva',
        avatar: '/api/placeholder/40/40',
        role: 'Marketing Student'
      },
      category: 'Internship',
      title: 'Summer Marketing Internship at Tech Startup',
      content: 'A fantastic opportunity for students interested in marketing! A fast-growing tech startup is offering a paid summer internship. Gain hands-on experience in digital marketing, social media, and content creation. Apply by April 30th! #Internship #Marketing #Tech',
      timestamp: '5 hours ago',
      likes: 28,
      comments: [
        { id: 201, user: 'tech_enthusiast', content: 'Applied! Hoping to get an interview!', timestamp: '2 hours ago' }
      ],
      liked: false
    },
    {
      id: 3,
      user: {
        name: 'girlsoncampus',
        avatar: '/api/placeholder/40/40',
        role: 'Official Campus Account'
      },
      category: 'Insight',
      title: 'Top 5 Tips for Writing a Standout College Essay',
      content: "Your college essay is your chance to shine! Check out our latest blog post for the top 5 tips on how to write a compelling and memorable essay that will impress admissions committees. #CollegeEssay #AdmissionsTips #HigherEducation",
      timestamp: '1 day ago',
      likes: 76,
      comments: [
        { id: 301, user: 'essay_writer', content: 'The tip about authentic storytelling really helped me with my application!', timestamp: '20 hours ago' },
        { id: 302, user: 'college_bound', content: 'Can you share examples of successful essays?', timestamp: '18 hours ago' },
        { id: 303, user: 'girlsoncampus', content: 'We\'ll be posting examples next week! Stay tuned.', timestamp: '12 hours ago' }
      ],
      liked: true
    },
    {
      id: 4,
      user: {
        name: 'JasonPark',
        avatar: '/api/placeholder/40/40',
        role: 'Student Ambassador'
      },
      category: 'Event',
      title: 'Virtual College Fair Next Week!',
      content: 'Don\'t miss the virtual college fair happening next week! Connect with representatives from over 100 colleges and universities from the comfort of your home. Register for free today! #CollegeFair #VirtualEvent #CollegeAdmissions',
      timestamp: '1 day ago',
      likes: 34,
      comments: [],
      liked: false
    },
    {
      id: 5,
      user: {
        name: 'girlsoncampus',
        avatar: '/api/placeholder/40/40',
        role: 'Official Campus Account'
      },
      category: 'Scholarship',
      title: 'Deadline Approaching: Art & Design Scholarship',
      content: 'Reminder! The deadline for the annual Art & Design Scholarship is just one week away. If you\'re a talented creative, don\'t miss this chance to win $2,000! #Scholarship #Art #Design',
      timestamp: '2 days ago',
      likes: 51,
      comments: [
        { id: 501, user: 'art_student', content: 'Is a portfolio required for application?', timestamp: '1 day ago' },
        { id: 502, user: 'girlsoncampus', content: 'Yes, you\'ll need to submit 3-5 pieces of your best work!', timestamp: '1 day ago' }
      ],
      liked: false
    },
  ];

  // Events data
  const events = [
    {
      id: 1,
      title: 'Ivy League Application Workshop',
      date: 'Tomorrow at 3 PM EST',
      location: 'Online (Zoom)',
    },
    {
      id: 2,
      title: 'Essay Writing Masterclass',
      date: 'Sat, Mar 15 at 2 PM EST',
      location: 'Online (Zoom)',
    },
    {
      id: 3,
      title: 'Interview Prep Session',
      date: 'Sun, Mar 16 at 1 PM EST',
      location: 'Online (Zoom)',
    },
  ];

  // Resources data
  const resources = [
    {
      id: 1,
      type: 'document',
      title: 'Common App Essay Guide',
      description: 'Complete guide to writing compelling essays',
    },
    {
      id: 2,
      type: 'video',
      title: 'Interview Success Tips',
      description: 'Expert advice for college interviews',
    },
    {
      id: 3,
      type: 'document',
      title: 'Scholarship Directory',
      description: 'Comprehensive list of opportunities',
    },
  ];

  // State for posts and new comment
  const [posts, setPosts] = useState(initialPosts);
  const [newComment, setNewComment] = useState({});
  const [newPost, setNewPost] = useState({
    title: '',
    content: '',
    category: 'General'
  });
  const [activeTab, setActiveTab] = useState('feed');

  // Handle like functionality
  const handleLike = (postId) => {
    setPosts(posts.map(post => {
      if (post.id === postId) {
        return {
          ...post,
          likes: post.liked ? post.likes - 1 : post.likes + 1,
          liked: !post.liked
        };
      }
      return post;
    }));
  };

  // Handle comment input change
  const handleCommentChange = (postId, value) => {
    setNewComment({
      ...newComment,
      [postId]: value
    });
  };

  // Handle comment submission
  const handleAddComment = (postId) => {
    if (!newComment[postId] || newComment[postId].trim() === '') return;

    setPosts(posts.map(post => {
      if (post.id === postId) {
        const newCommentObj = {
          id: Date.now(),
          user: 'current_user', // In a real app, this would be the logged-in user
          content: newComment[postId],
          timestamp: 'Just now'
        };

        return {
          ...post,
          comments: [...post.comments, newCommentObj]
        };
      }
      return post;
    }));

    // Clear the comment input
    setNewComment({
      ...newComment,
      [postId]: ''
    });
  };

  // Handle new post input changes
  const handlePostInputChange = (e) => {
    const { name, value } = e.target;
    setNewPost({
      ...newPost,
      [name]: value
    });
  };

  // Handle new post submission
  const handleCreatePost = (e) => {
    e.preventDefault();

    if (!newPost.title.trim() || !newPost.content.trim()) return;

    const newPostObj = {
      id: Date.now(),
      user: {
        name: 'current_user', // In a real app, this would be the logged-in user
        avatar: '/api/placeholder/40/40',
        role: 'Student'
      },
      category: newPost.category,
      title: newPost.title,
      content: newPost.content,
      timestamp: 'Just now',
      likes: 0,
      comments: [],
      liked: false
    };

    setPosts([newPostObj, ...posts]);

    // Clear the form
    setNewPost({
      title: '',
      content: '',
      category: 'General'
    });
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Sidebar */}
        <LeftSidebar activeTab={activeTab} setActiveTab={setActiveTab} />

        {/* Main Content - Original Feed Implementation */}
        <div className="lg:col-span-6">
          {/* Create Post Form - LinkedIn Style */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center">
                <div className="bg-gradient-to-r from-indigo-400 to-purple-500 w-10 h-10 rounded-full flex items-center justify-center text-white text-lg font-bold mr-3">
                  Y
                </div>
                <button
                  onClick={() => document.getElementById('postInput').focus()}
                  className="bg-gray-100 hover:bg-gray-200 rounded-full py-3 px-4 text-gray-500 text-sm text-left w-full"
                >
                  Start a post...
                </button>
              </div>
            </div>

            <form onSubmit={handleCreatePost} className="px-4 pb-3">
              <div className="mb-3 mt-2">
                <input
                  id="postInput"
                  type="text"
                  name="title"
                  placeholder="Add a title..."
                  className="w-full px-3 py-2 bg-gray-50 border-none rounded-md text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500"
                  value={newPost.title}
                  onChange={handlePostInputChange}
                />
              </div>
              <div className="mb-3">
                <textarea
                  name="content"
                  placeholder="What's on your mind? Keep it 💯"
                  rows="3"
                  className="w-full px-3 py-2 bg-gray-50 border-none rounded-md text-sm focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500"
                  value={newPost.content}
                  onChange={handlePostInputChange}
                ></textarea>
              </div>

              <div className="flex justify-between items-center">
                <select
                  name="category"
                  className="px-3 py-2 bg-gray-50 border-none rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={newPost.category}
                  onChange={handlePostInputChange}
                >
                  <option value="General">✨ General</option>
                  <option value="Scholarship">💰 Scholarship</option>
                  <option value="Internship">💼 Internship</option>
                  <option value="Event">🎉 Event</option>
                  <option value="Insight">💡 Insight</option>
                </select>

                <div className="flex gap-2">
                  <button type="button" className="text-gray-500 hover:text-gray-700 p-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </button>
                  <button type="button" className="text-gray-500 hover:text-gray-700 p-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                  </button>
                  <button
                    type="submit"
                    className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white font-medium py-2 px-4 rounded-full text-sm transition duration-200"
                  >
                    Post
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Feed Posts */}
          {posts.map((post) => (
            <div key={post.id} className="bg-white rounded-lg shadow mb-4 overflow-hidden">
              {/* Post Header */}
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-lg font-bold mr-3 ${
                    post.user.name === 'girlsoncampus'
                      ? 'bg-gradient-to-r from-indigo-400 to-purple-500'
                      : post.user.name === 'MariaSilva'
                      ? 'bg-gradient-to-r from-blue-400 to-teal-400'
                      : 'bg-gradient-to-r from-violet-400 to-fuchsia-500'
                  }`}>
                    {post.user.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900 flex items-center">
                      {post.user.name}
                      {post.user.name === 'girlsoncampus' && (
                        <span className="ml-1 bg-indigo-100 text-indigo-800 text-xs px-1.5 rounded-md">✓ Official</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center">
                      <span>{post.user.role}</span>
                      <span className="mx-1">•</span>
                      <span>{post.timestamp}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Post Content */}
              <div className="p-4">
                <div className="flex items-center mb-2">
                  <span className={`text-xs font-medium rounded-full px-2 py-1 ${
                    post.category === 'Scholarship' ? 'bg-green-100 text-green-800' :
                    post.category === 'Internship' ? 'bg-blue-100 text-blue-800' :
                    post.category === 'Event' ? 'bg-indigo-100 text-indigo-800' :
                    post.category === 'Insight' ? 'bg-violet-100 text-violet-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {post.category === 'Scholarship' && '💰 '}
                    {post.category === 'Internship' && '💼 '}
                    {post.category === 'Event' && '🎉 '}
                    {post.category === 'Insight' && '💡 '}
                    {post.category}
                  </span>
                </div>
                <h2 className="text-lg font-semibold text-gray-800 mb-2">{post.title}</h2>
                <p className="text-gray-700 text-sm whitespace-pre-line">
                  {post.content}
                  {post.content.includes('#') && (
                    <div className="mt-2">
                      {post.content.match(/#[a-zA-Z0-9]+/g)?.map((hashtag, i) => (
                        <span key={i} className="inline-block mr-2 text-indigo-600 hover:underline cursor-pointer">
                          {hashtag}
                        </span>
                      ))}
                    </div>
                  )}
                </p>
              </div>

              {/* Post Stats */}
              <div className="px-4 py-2 border-t border-b border-gray-100 text-xs text-gray-500">
                {post.likes > 0 && (
                  <div className="flex items-center">
                    <div className="flex -space-x-1 mr-1">
                      <div className="w-5 h-5 rounded-full bg-gradient-to-r from-indigo-400 to-purple-500 flex items-center justify-center">
                        <span className="text-white text-xs">❤️</span>
                      </div>
                      <div className="w-5 h-5 rounded-full bg-gradient-to-r from-blue-400 to-indigo-500 flex items-center justify-center">
                        <span className="text-white text-xs">👍</span>
                      </div>
                    </div>
                    <span>{post.likes} vibes</span>
                  </div>
                )}
                {post.comments.length > 0 && (
                  <div className="mt-1">
                    <span>{post.comments.length} {post.comments.length === 1 ? 'comment' : 'comments'}</span>
                  </div>
                )}
              </div>

              {/* Post Actions */}
              <div className="flex border-b border-gray-100">
                <button
                  className={`flex-1 py-2 flex items-center justify-center text-sm font-medium ${
                    post.liked
                      ? 'text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-purple-500'
                      : 'text-gray-500 hover:text-gray-600'
                  }`}
                  onClick={() => handleLike(post.id)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 mr-1 ${
                    post.liked ? 'fill-indigo-500' : 'fill-none stroke-current stroke-2'
                  }`} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                  </svg>
                  {post.liked ? 'Vibing' : 'Vibe'}
                </button>
                <button className="flex-1 py-2 flex items-center justify-center text-sm font-medium text-gray-500 hover:text-gray-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Comment
                </button>
                <button className="flex-1 py-2 flex items-center justify-center text-sm font-medium text-gray-500 hover:text-gray-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  Share
                </button>
              </div>

              {/* Comments Section */}
              <div className="p-4 bg-gray-50">
                {post.comments.length > 0 && (
                  <div className="mb-3">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">💬 Comments</h3>
                    {post.comments.map((comment) => (
                      <div key={comment.id} className="bg-white p-3 rounded-lg mb-2 border border-gray-200">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2 ${
                              comment.user === 'girlsoncampus'
                                ? 'bg-gradient-to-r from-indigo-400 to-purple-500'
                                : comment.user === 'sarah_stem' || comment.user === 'essay_writer'
                                ? 'bg-gradient-to-r from-blue-400 to-teal-400'
                                : 'bg-gradient-to-r from-violet-400 to-fuchsia-500'
                            }`}>
                              {comment.user.charAt(0).toUpperCase()}
                            </div>
                            <span className="font-medium text-sm text-gray-800">@{comment.user}</span>
                          </div>
                          <span className="text-xs text-gray-500">{comment.timestamp}</span>
                        </div>
                        <p className="text-sm text-gray-700 pl-8">{comment.content}</p>
                        <div className="mt-2 pl-8 flex space-x-3 text-xs text-gray-500">
                          <button className="hover:text-gray-800">Like</button>
                          <button className="hover:text-gray-800">Reply</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Comment */}
                <div className="flex">
                  <div className="mr-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-indigo-400 to-purple-500 flex items-center justify-center text-white font-bold">
                      Y
                    </div>
                  </div>
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      placeholder="Add a comment... (use emojis for bonus points ✨)"
                      className="w-full px-3 py-2 bg-white border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      value={newComment[post.id] || ''}
                      onChange={(e) => handleCommentChange(post.id, e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddComment(post.id)}
                    />
                    <button
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-full hover:from-indigo-600 hover:to-purple-600 transition duration-200"
                      onClick={() => handleAddComment(post.id)}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Right Sidebar */}
        <RightSidebar events={events} resources={resources} />
      </div>
    </div>
  );
};

export default FeedPage;